import { memo } from 'react'

import { Box, Stack, Text } from '@mantine/core'

import { countFormatter, usdFormatter } from '@repo/common/helpers/formatters'
import { useCreatePosition } from '@repo/common/queries/fission_dex'
import type {
  AddressableSymbol,
  FundSymbol,
} from '@repo/common/queries/products'

import { ContentBox } from '@/components/ContentBox'
import { LiquidityChart } from '@/components/LiquidityChart'
import {
  type ExtraAuthSyncingProps,
  PanelAuthSyncing,
  useSkipAuthSyncing,
} from '@/panels/PanelAuthSyncing'
import {
  PanelCheckWalletRisk,
  useSkipWalletRisk,
} from '@/panels/PanelCheckWalletRisk'
import {
  PanelConfirmEmail,
  useSkipConfirmEmail,
} from '@/panels/PanelConfirmEmail'
import {
  PanelLocationAttestation,
  useSkipLocationVerify,
} from '@/panels/PanelLocationAttestation'
import {
  type ExtraPanelPermitProps,
  PanelPermit2,
  useSkipPermit2,
} from '@/panels/PanelPermit2'
import {
  PanelRegisterWallet,
  useRegisterWalletTitle,
  useSkipRegisterWallet,
} from '@/panels/PanelRegisterWallet'
import { PanelSignup, useSkipLogin } from '@/panels/PanelSignup'

import {
  type BasePanelProps,
  normalize_progress,
  NumberRow,
  type StepsType,
  useFlowControl,
} from '../flow_helpers'
import { FlowButton } from '../flow_helpers'
import { allowed_kyc_levels } from '../Swap'

import { useManualFields } from './useManualFields'

type ManualPanelProps = BasePanelProps &
  ExtraAuthSyncingProps &
  ExtraPanelPermitProps & {
    symbol_in2: AddressableSymbol
  }

const steps: StepsType<ManualPanelProps> = normalize_progress([
  {
    title: 'Signup',
    Panel: PanelSignup,
    progress: 10,
    useSkipCheck: useSkipLogin,
  },
  {
    title: 'Confirm Email',
    Panel: PanelConfirmEmail,
    progress: 10,
    useSkipCheck: useSkipConfirmEmail,
  },
  {
    title: useRegisterWalletTitle,
    Panel: PanelRegisterWallet,
    progress: 5,
    useSkipCheck: useSkipRegisterWallet,
  },
  {
    title: 'Location Verification',
    Panel: PanelLocationAttestation,
    progress: 5,
    useSkipCheck: useSkipLocationVerify,
  },
  {
    title: 'Wallet Check',
    Panel: PanelCheckWalletRisk,
    progress: 5,
    useSkipCheck: useSkipWalletRisk,
  },
  {
    title: 'Enable Permit2',
    Panel: PanelPermit2,
    progress: 5,
    useSkipCheck: useSkipPermit2,
  },
  {
    title: 'Enable Permit2',
    Panel: ({ symbol_in2, ...args }: ManualPanelProps) => (
      <PanelPermit2 {...args} symbol_in={symbol_in2} />
    ),
    progress: 5,
    useSkipCheck: ({ symbol_in2 }: { symbol_in2: AddressableSymbol }) =>
      useSkipPermit2({ symbol_in: symbol_in2 }),
  },
  {
    title: 'Auth Syncing',
    Panel: PanelAuthSyncing,
    progress: 5,
    useSkipCheck: useSkipAuthSyncing,
  },
])

export const ManualPosition = memo<{
  fund_symbol: FundSymbol
  onCompleted: () => void
}>(
  // eslint-disable-next-line max-lines-per-function, complexity
  function ManualPosition({ fund_symbol, onCompleted }) {
    const {
      form,
      wallet_isConnected,
      form_valid,
      paused,
      error_reason,
      price_low_focus,
      price_high_focus,
      deposit_fund_focus,
      deposit_usdc_focus,
      price_limits,
      disabled_usdc,
      disabled_fund,
      balance_fund,
      balance_usdc,
      fund_price,
      is_loading,
    } = useManualFields({ fund_symbol })

    const create_position = useCreatePosition({ fund_symbol })

    const { submitted, onSubmit, modal } = useFlowControl({
      steps,
      extra_args: {
        allowed_kyc_levels,
        symbol_in: 'USDC',
        symbol_in2: fund_symbol,
      },
      final_action: () =>
        create_position.mutateAsync(form.getValues()).then(onCompleted),
    })

    return (
      <Stack maw="100%">
        {modal}
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack
            maw="calc(100vw - 2 * var(--app-body-margin))"
            w="500px"
            gap="xxs"
          >
            <Stack gap="0">
              <Text size="md">Set Price Range</Text>
              <Stack gap="xxs">
                <ContentBox
                  p="lg"
                  gap="xs"
                  direction="row"
                  style={{ zIndex: 10 }}
                >
                  <Stack gap="0">
                    <Text size="xs" opacity={0.7}>
                      Current price
                    </Text>
                    <Text size="xl" lh="1.42">
                      {countFormatter(fund_price ?? 0, {
                        compact: false,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                    <Text size="xs" opacity={0.7}>
                      USDC per {fund_symbol}
                    </Text>
                  </Stack>
                  <Box flex="1 1" pr="xs" mb="-30px">
                    <LiquidityChart
                      height={75}
                      graph_font_size="var(--mantine-font-size-xs)"
                      fund_symbol={fund_symbol}
                    />
                  </Box>
                </ContentBox>
                <ContentBox
                  p="lg"
                  gap="xs"
                  ref={price_low_focus.ref}
                  mod={{
                    focused: price_low_focus.focused,
                    error: Boolean(form.getInputProps('price_low').error),
                  }}
                >
                  <Text size="xs" lh="1" opacity={0.7}>
                    Low price
                  </Text>
                  <NumberRow
                    {...form.getInputProps('price_low', { withError: false })}
                    disabled={submitted || is_loading}
                    aria-label="low price"
                    allowNegative={false}
                    decimalScale={2}
                    placeholder="0"
                  />
                  <Text size="xs" opacity={0.7}>
                    Acceptable range: below{' '}
                    {usdFormatter(price_limits.price_first_lower_outside)} or
                    above {usdFormatter(price_limits.price_first_upper_outside)}
                  </Text>
                </ContentBox>
                <ContentBox
                  p="lg"
                  gap="xs"
                  ref={price_high_focus.ref}
                  mod={{
                    focused: price_high_focus.focused,
                    error: Boolean(form.getInputProps('price_high').error),
                  }}
                >
                  <Text size="xs" lh="1" opacity={0.7}>
                    High price
                  </Text>
                  <NumberRow
                    {...form.getInputProps('price_high', { withError: false })}
                    disabled={submitted || is_loading}
                    aria-label="high price"
                    allowNegative={false}
                    decimalScale={2}
                  />
                  <Text size="xs" opacity={0.7}>
                    Acceptable range: below{' '}
                    {usdFormatter(price_limits.price_first_lower_outside)} or
                    above {usdFormatter(price_limits.price_first_upper_outside)}
                  </Text>
                </ContentBox>
              </Stack>
            </Stack>
            <Stack gap="0">
              <Text size="md">Deposit amounts</Text>
              <Stack gap="xxs">
                <ContentBox
                  p="xs"
                  gap="0"
                  ref={deposit_usdc_focus.ref}
                  mod={{
                    focused: deposit_usdc_focus.focused,
                    error: Boolean(
                      form.getInputProps('deposit_usdc_tokens').error,
                    ),
                  }}
                  direction="row"
                  opacity={disabled_usdc ? 0.2 : 1}
                >
                  <NumberRow
                    {...form.getInputProps('deposit_usdc_tokens', {
                      withError: false,
                    })}
                    symbol="USDC"
                    disabled={disabled_usdc || submitted || is_loading}
                    aria-label="deposit usdc amount"
                    allowNegative={false}
                    decimalScale={2}
                    balance_number={balance_usdc}
                    balance_maximumFractionDigits={2}
                    max_button={() =>
                      form.setFieldValue('deposit_usdc_tokens', balance_usdc)
                    }
                  />
                </ContentBox>
                <ContentBox
                  p="xs"
                  gap="0"
                  ref={deposit_fund_focus.ref}
                  mod={{
                    focused: deposit_fund_focus.focused,
                    error: Boolean(
                      form.getInputProps('deposit_fund_tokens').error,
                    ),
                  }}
                  direction="row"
                  opacity={disabled_fund ? 0.2 : 1}
                >
                  <NumberRow
                    {...form.getInputProps('deposit_fund_tokens', {
                      withError: false,
                    })}
                    symbol={fund_symbol}
                    disabled={disabled_fund || submitted || is_loading}
                    aria-label="deposit funds amount"
                    allowNegative={false}
                    decimalScale={2}
                    balance_number={balance_fund}
                    balance_maximumFractionDigits={4}
                    max_button={() =>
                      form.setFieldValue('deposit_fund_tokens', balance_fund)
                    }
                  />
                </ContentBox>
              </Stack>
            </Stack>
            <FlowButton
              wallet_isConnected={wallet_isConnected}
              form_valid={form_valid}
              is_loading={is_loading}
              paused={paused}
              error_reason={error_reason}
              mutation_query={create_position}
              action_word="Create Position"
            />
          </Stack>
        </form>
      </Stack>
    )
  },
)
